/**
 * @fileoverview User Management cache helpers.
 * @description The one place that knows which cached queries a lifecycle command (Deactivate or
 * Reactivate) changes outside the open dialog: the directory, the caller's team and their summaries
 * and filter options, the Invitations list (deactivating a Pending user revokes their invitation;
 * reactivating can issue a new one), and the Sales roster (the user turns Inactive or Active, and
 * so do the owner pickers built from it) and targets (a deactivated user's outstanding targets
 * are transferred, so their owner changes). A new query that a lifecycle command affects is added
 * here and nowhere else. The pickers that list who may be chosen (the eligible
 * reporting managers, since a deactivated manager must stop being offered in Add, Edit and
 * Reactivate and a reactivated one start; each user's edit options; and direct reports) and the
 * users' cached details are marked stale without being refetched, so the dialog that is about to
 * close does not re-read anything (a new ETag would remount its form), while the next open still
 * loads fresh data.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { USERS_QUERY_KEYS } from '@/features/users/constants/users-query-keys.constants'
import { ELIGIBLE_REPORTING_MANAGERS_QUERY_KEY } from '@/shared/constants/directory-options-query-keys.constants'
import { INVITATIONS_QUERY_KEY } from '@/shared/constants/invitations-query-keys.constants'
import {
  SALES_DASHBOARD_QUERY_KEY,
  SALES_ROSTER_QUERY_KEY,
  TARGETS_QUERY_KEY,
} from '@/shared/constants/sales-query-keys.constants'

import type { QueryClient } from '@tanstack/react-query'

/**
 * @description Refreshes the lists and summaries a lifecycle command changes, and marks the
 * pickers and every cached user's details stale without refetching what the open dialog shows.
 * @param {QueryClient} queryClient - The query client to refresh.
 */
export function refreshAfterLifecycleChange(queryClient: QueryClient): void {
  const refreshed = [
    USERS_QUERY_KEYS.directory,
    USERS_QUERY_KEYS.summary,
    USERS_QUERY_KEYS.filterOptions,
    USERS_QUERY_KEYS.myTeam,
    INVITATIONS_QUERY_KEY,
    SALES_ROSTER_QUERY_KEY,
    TARGETS_QUERY_KEY,
  ]
  for (const queryKey of refreshed) {
    void queryClient.invalidateQueries({ queryKey })
  }

  const markedStale = [
    USERS_QUERY_KEYS.details,
    USERS_QUERY_KEYS.directReports,
    USERS_QUERY_KEYS.allEditOptions,
    ELIGIBLE_REPORTING_MANAGERS_QUERY_KEY,
    SALES_DASHBOARD_QUERY_KEY,
  ]
  for (const queryKey of markedStale) {
    void queryClient.invalidateQueries({ queryKey, refetchType: 'none' })
  }
}
